export const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const computeCommandeTotal = (commande) => {
  const directTotal = toNumber(commande?.total);
  if (directTotal !== null) return directTotal;

  const finalPrice = toNumber(commande?.prixFinal);
  if (finalPrice !== null) return finalPrice;

  const basePrice = toNumber(commande?.prestation?.prix);
  if (basePrice === null) return 0;

  const participantsCount = toNumber(commande?.participants);
  return basePrice * (participantsCount ?? 1);
};

export const isCommandeAnnulee = (cmd) => {
  const statut = cmd?.statut?.toUpperCase?.() || '';
  return statut === 'ANNULEE' || statut === 'ANNULE';
};

export const formatEuro = (amount) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);

export const buildCommandesInsights = (commandes = []) => {
  const valides = commandes.filter((c) => !isCommandeAnnulee(c));
  const annulees = commandes.length - valides.length;

  // Répartition sur TOUTES les lignes filtrées (y compris annulées)
  const parStatut = commandes.reduce((acc, cmd) => {
    const key = (cmd.statut || 'INCONNU').toUpperCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const rdv = valides.filter((c) => c.type === 'RENDEZ_VOUS');
  const commandesProduits = valides.filter((c) => c.type !== 'RENDEZ_VOUS');

  const ca = valides.reduce((sum, c) => sum + computeCommandeTotal(c), 0);
  const panierMoyen = valides.length ? ca / valides.length : 0;

  const recentes = [...commandes]
    .sort((a, b) => new Date(b.dateCommande || b.createdAt || 0) - new Date(a.dateCommande || a.createdAt || 0));

  return {
    totalToutes: commandes.length,
    total: valides.length,
    annulees,
    parStatut,
    rdvCount: rdv.length,
    produitsCount: commandesProduits.length,
    ca,
    panierMoyen,
    recentes,
  };
};

export const filterCommandes = (commandes, { search = '', statut = '', type = '', periode = 'all' }) => {
  const q = search.trim().toLowerCase();
  const now = new Date();
  const periodeStart = (() => {
    if (periode === '7j') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return d;
    }
    if (periode === '30j') {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d;
    }
    if (periode === '90j') {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return d;
    }
    return null;
  })();

  return commandes.filter((cmd) => {
    if (periodeStart) {
      const d = new Date(cmd.dateCommande || cmd.createdAt || 0);
      if (d < periodeStart) return false;
    }
    if (statut && (cmd.statut || '').toUpperCase() !== statut.toUpperCase()) return false;
    if (type === 'RDV' && cmd.type !== 'RENDEZ_VOUS') return false;
    if (type === 'PRODUIT' && cmd.type === 'RENDEZ_VOUS') return false;
    if (!q) return true;
    const hay = [
      cmd.numero,
      cmd.id,
      cmd.statut,
      cmd.type,
      cmd.user?.nom,
      cmd.user?.prenom,
      cmd.user?.email,
      cmd.prestation?.nom,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
};

export const filterCatalogItems = (items, { search = '', type = '' }) => {
  const q = search.trim().toLowerCase();
  return items.filter((item) => {
    if (type && item.typePrestation !== type) return false;
    if (!q) return true;
    return [item.nom, item.description, item.typePrestation, String(item.prix)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
};

export const buildCaInsights = (commandes = []) => {
  const insights = buildCommandesInsights(commandes);
  const valides = commandes.filter((c) => !isCommandeAnnulee(c));

  const parMois = valides.reduce((acc, cmd) => {
    const d = new Date(cmd.dateCommande || cmd.createdAt || Date.now());
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[key]) acc[key] = { label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }), ca: 0, count: 0 };
    acc[key].ca += computeCommandeTotal(cmd);
    acc[key].count += 1;
    return acc;
  }, {});

  const moisSorted = Object.entries(parMois)
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, 6)
    .map(([, v]) => v);

  const caRdv = valides
    .filter((c) => c.type === 'RENDEZ_VOUS')
    .reduce((s, c) => s + computeCommandeTotal(c), 0);
  const caProduits = insights.ca - caRdv;

  return { ...insights, moisSorted, caRdv, caProduits };
};
